import React, { useEffect, useState } from 'react';

import { Badge, Box, Button, Flex, Paper, Space, Text, Title } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { InfoComponent } from 'components/InfoComponent';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { config } from 'environment/config';
import { ShopifyIcon } from 'icons/ShopifyIcon';
import { CancelationModal } from 'pages/Settings/SettingTabs/BillingSettingsTab/CancelationModal';
import { SettingsSectionWrapper } from 'pages/Settings/SettingTabs/SettingsSectionWrapper';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { ShopifyService } from 'services/shopify-service';
import { SubscriptionService } from 'services/subscription-service';
import { SHOPIFY_STORES_QUERY_KEY, SUBSCRIPTION_BALANCE_QUERY_KEY } from 'shared/constants/query-keys';
import { BillingSession } from 'shared/interfaces/CustomerPortalSession';
import { ProductName, RecurringInterval } from 'shared/interfaces/Product';
import { formatDate } from 'shared/utils/format';
import { navigateToPricingPage } from 'shared/utils/navigation';

const TEN_SECONDS = 10000;

export function BillingSettingsTab() {
  const { t } = useTranslation();
  const [subscription, setSubscription] = useSubscriptionContext();
  const [isCancelationModalOpened, setIsCancelationModalOpened] = useState(false);

  const balanceQuery = useQuery({
    queryKey: [SUBSCRIPTION_BALANCE_QUERY_KEY],
    queryFn: () => SubscriptionService.getSubscriptionBalance()
  });

  const shopifyStoresQuery = useQuery({
    queryKey: [SHOPIFY_STORES_QUERY_KEY],
    queryFn: () => ShopifyService.getAllStores(),
    retry: false
  });

  useEffect(() => {
    posthog.capture('subscription_viewed_on_billing_settings', {
      product: subscription.product.name,
      recurringInterval: subscription.product.recurringInterval,
      price: subscription.product.price
    });
  }, []);

  const createBillingPortalSession = useMutation({
    mutationFn: async () => await SubscriptionService.createBillingPortalSession(),
    onSuccess: async (billingSession: BillingSession) => onBillingSessionSuccess(billingSession)
  });

  const updateSubscriptionSession = useMutation({
    mutationFn: async () => await SubscriptionService.createBillingPortalSession({ updateSubscription: true }),
    onSuccess: async (billingSession: BillingSession) => onBillingSessionSuccess(billingSession)
  });

  const reactivateSubscriptionMutation = useMutation({
    mutationFn: SubscriptionService.reactivateSubscription,
    onSuccess: () => {
      // Temporary data since the subscription update is triggered by the webhook.
      // This is done to display the cancelation UI.
      setSubscription((prevSubscription) => ({ ...prevSubscription, cancelAtRenewDate: false }));
      NotificationService.showResult(true, 'FEED_BACK.SUBSCRIPTION.SUCCESS.REACTIVATE');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SUBSCRIPTION.FAILURE.REACTIVATE')
  });

  const calculatePrice = () => {
    if (
      (!subscription.promoCode?.numberOfMonths && subscription.product.recurringInterval === RecurringInterval.MONTH) ||
      (subscription.promoCode?.numberOfMonths && subscription.product.recurringInterval === RecurringInterval.YEAR)
    )
      return subscription.cancelAtRenewDate ? 0 : subscription.product.price;

    return subscription.promoCode
      ? subscription.product.price - subscription.product.price * (subscription.promoCode.discountPercentage / 100)
      : subscription.product.price;
  };

  const onBillingSessionSuccess = async (billingSession: BillingSession) => {
    window.location.href = billingSession.url;
    // Wait 10 seconds so that the loader can still be seen while redirecting
    await new Promise((resolve) => setTimeout(resolve, TEN_SECONDS));
  };

  if (shopifyStoresQuery.isLoading) return <FullScreenLoader />;

  return (
    <>
      <SettingsSectionWrapper
        title={t('SETTINGS.BILLING.SUMMARY.TITLE')}
        titleNextComponent={
          subscription.cancelAtRenewDate ? (
            <Badge color="red" tt="capitalize">
              {t('SETTINGS.BILLING.SUMMARY.CANCEL_SUBSCRIPTION.TITLE')}
            </Badge>
          ) : undefined
        }
        rightComponent={
          <Flex align="center" gap={20}>
            <Button
              variant="default"
              onClick={() => navigateToPricingPage()}
              rightIcon={<IconExternalLink style={{ width: 16, height: 16 }} />}>
              {t('SETTINGS.BILLING.COMPARE_PLANS')}
            </Button>
            {(!shopifyStoresQuery.data || shopifyStoresQuery.data.length === 0) && (
              <Button
                variant="filled"
                loading={createBillingPortalSession.isLoading}
                onClick={() => createBillingPortalSession.mutate()}>
                {t('SETTINGS.BILLING.OPEN_BILLING_PORTAL')}
              </Button>
            )}
          </Flex>
        }>
        {shopifyStoresQuery.data && shopifyStoresQuery.data.length > 0 ? (
          <Paper>
            <Flex direction="row" gap="lg" align="center">
              <Text size="md">{t('SETTINGS.BILLING.SHOPIFY.TITLE')}</Text>
              <Button
                onClick={() => window.open(config.shopifyPricingPlanUrl, '_blank')}
                variant="default"
                rightIcon={<ShopifyIcon />}>
                {t('SETTINGS.BILLING.SHOPIFY.MANAGE_SUBSCRIPTION')}
              </Button>
            </Flex>
          </Paper>
        ) : (
          <>
            <Paper
              p="lg"
              w="fit-content"
              shadow="sm"
              sx={(theme) => ({ border: `0.0625rem solid ${theme.colors.gray[3]}` })}>
              <Flex direction="column" justify="space-between" h="100%" w="100%">
                <Box>
                  <Box>
                    <Flex direction="row" align="center" justify="space-between">
                      <Title order={3}>{t(`PRODUCTS.${subscription.product.name.toLocaleUpperCase()}`)}</Title>
                      {subscription.trialDetails && subscription.trialDetails.endDate >= Date.now() && (
                        <Badge color="teal" tt="capitalize">
                          {t('SETTINGS.BILLING.SUMMARY.TRIAL')}
                        </Badge>
                      )}
                    </Flex>
                    <Flex direction="row" align="end">
                      <Title order={4} weight={500}>
                        {calculatePrice()}$US{' '}
                        <Text span c="dimmed">
                          {t('SETTINGS.BILLING.SUMMARY.NEXT_PAYMENT_DUE', { date: formatDate(subscription.renewDate) })}
                        </Text>
                      </Title>
                    </Flex>
                  </Box>

                  {balanceQuery.data && balanceQuery.data.amount > 0 && (
                    <Box mt="xs">
                      <Flex direction="row" align="center">
                        <Title order={5} weight={500}>
                          {t('SETTINGS.BILLING.SUMMARY.BALANCE')}
                        </Title>
                        <Space w={5} />
                        <InfoComponent
                          width={200}
                          contentTranslationKey={'SETTINGS.BILLING.SUMMARY.BALANCE_INFO'}
                          position="right"
                        />
                      </Flex>
                      <Title order={4} weight={500}>
                        {balanceQuery.data.amount}$US
                      </Title>
                    </Box>
                  )}
                </Box>

                <Flex direction="row" align="center" mt={30}>
                  {subscription.product.name !== ProductName.STARTER &&
                    (subscription.cancelAtRenewDate ? (
                      <Button
                        loading={reactivateSubscriptionMutation.isLoading}
                        variant="outline"
                        size="xs"
                        color="green"
                        onClick={() => reactivateSubscriptionMutation.mutate()}>
                        {t('SETTINGS.BILLING.REACTIVATE_SUBSCRIPTION')}
                      </Button>
                    ) : (
                      <Button variant="outline" color="red" size="xs" onClick={() => setIsCancelationModalOpened(true)}>
                        {t('SETTINGS.BILLING.CANCEL_SUBSCRIPTION.TITLE')}
                      </Button>
                    ))}
                  <Button
                    ml="xs"
                    size="xs"
                    loading={updateSubscriptionSession.isLoading}
                    onClick={() => updateSubscriptionSession.mutate()}>
                    {t('SETTINGS.BILLING.UPDATE_SUBSCRIPTION')}
                  </Button>
                </Flex>
              </Flex>
            </Paper>
            <Space h={10} />
          </>
        )}
      </SettingsSectionWrapper>
      <CancelationModal
        isOpen={isCancelationModalOpened}
        onClose={() => setIsCancelationModalOpened(false)}
        isMonthly={subscription.product.recurringInterval === RecurringInterval.MONTH}
      />
    </>
  );
}
